import { useQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { Plus, Sparkles } from "lucide-react";
import { AgentCard } from "@/components/AgentCard";
import { HomepageSearch } from "@/components/HomepageSearch";
import { JsonLd } from "@/components/JsonLd";
import { useListAgentDialog } from "@/components/ListAgentDialog";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { PublicPageLayout } from "@/components/PublicPageLayout";
import { PublicFooter } from "@/components/public-layout";
import {
	CATEGORY_ICONS,
	coerceAgentCategories,
	getAgentCategoryLabel,
	normalizeAgentCategory,
} from "@/lib/agent-categories";
import { homepageLeaderboardQueryOptions } from "@/lib/leaderboard.query";
import { breadcrumbListSchema } from "@/lib/schema";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

export const Route = createFileRoute("/categories/$category")({
	component: CategoryPage,
	beforeLoad: ({ params }) => {
		const category = normalizeAgentCategory(params.category);
		if (!category) {
			throw notFound();
		}
	},
	loader: ({ context }) =>
		context.queryClient.ensureQueryData(homepageLeaderboardQueryOptions),
	head: ({ params, matches }) => {
		const category = normalizeAgentCategory(params.category);
		if (!category) return {};
		const categoryLabel = getAgentCategoryLabel(category);
		const internalPath = `/categories/${category}`;
		const { canonicalUrl } = getLocalizedSeoUrls({ matches, internalPath });
		const title = `${categoryLabel} AI Agents - AgentMRR`;
		const description = `Browse revenue-generating ${categoryLabel} AI agents ranked by MRR. Verified earnings, public rankings, and Agent Auth access.`;
		return {
			meta: [
				{ title },
				{ name: "description", content: description },
				{ property: "og:title", content: title },
				{ property: "og:description", content: description },
				{ property: "og:type", content: "website" },
				{ property: "og:site_name", content: "AgentMRR" },
				{ property: "og:image", content: ogImageUrl("default") },
				{ property: "og:url", content: canonicalUrl },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("default") },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath }),
		};
	},
});

function CategoryPage() {
	const { category: categorySlug } = Route.useParams();
	const category = normalizeAgentCategory(categorySlug);
	if (!category) {
		throw notFound();
	}
	const categoryLabel = getAgentCategoryLabel(category);
	const { openListAgentDialog } = useListAgentDialog();

	const { data } = useQuery(homepageLeaderboardQueryOptions);
	const agents = (data?.agents ?? []).filter((agent) =>
		coerceAgentCategories(agent.categories).includes(category),
	);

	const Icon = CATEGORY_ICONS[category] ?? Sparkles;
	const count = agents.length;

	return (
		<PublicPageLayout>
			<JsonLd
				data={breadcrumbListSchema([
					{ name: "Home", path: "/" },
					{ name: "Categories", path: "/categories" },
					{
						name: `${categoryLabel} agents`,
						path: `/categories/${category}`,
					},
				])}
			/>
			<section className="relative bg-background pt-8 pb-6 sm:pt-10">
				<div className="relative mx-auto w-full max-w-5xl">
					<PageBreadcrumb
						className="pb-4"
						items={[
							{ label: "Categories", to: "/categories" },
							{ label: categoryLabel },
						]}
						trailing={
							<span className="tabular-nums text-muted-foreground">
								{String(count)}
								<span className="ml-1">{count === 1 ? "agent" : "agents"}</span>
							</span>
						}
					/>

					<div className="border-t border-border pt-6">
						<p className="max-w-xl text-base leading-[1.55] text-muted-foreground">
							{count > 0
								? `${count} ${count === 1 ? "agent" : "agents"} in ${categoryLabel}, ranked by verified MRR.`
								: `Be the first to list a ${categoryLabel.toLowerCase()} agent and rank by verified MRR.`}
						</p>

						<div className="mt-6 flex flex-col gap-3 sm:flex-row">
							<div className="flex-1">
								<HomepageSearch joined />
							</div>
							<button
								type="button"
								onClick={() => openListAgentDialog("user")}
								className="inline-flex min-h-11 items-center justify-center gap-2 bg-brand px-5 font-mono text-sm uppercase tracking-[0.14em] text-background transition-colors hover:bg-brand-hover"
							>
								<Plus className="size-3.5" aria-hidden="true" />
								Add agent
							</button>
						</div>
					</div>
				</div>
			</section>

			<main className="mx-auto w-full max-w-5xl pt-8 pb-20">
				{count === 0 ? (
					<div className="border border-border bg-card p-10 text-center">
						<Icon className="mx-auto size-5 text-muted-foreground" />
						<p className="mt-4 font-mono text-sm uppercase tracking-[0.14em] text-foreground">
							No {categoryLabel.toLowerCase()} agents yet
						</p>
						<p className="mt-2 text-base text-muted-foreground">
							Be the first to claim a spot in this category.
						</p>
						<button
							type="button"
							onClick={() => openListAgentDialog("user")}
							className="mt-6 inline-flex items-center justify-center gap-2 bg-brand px-5 py-2.5 font-mono text-sm uppercase tracking-[0.14em] text-background transition-colors hover:bg-brand-hover"
						>
							<Plus className="size-3.5" aria-hidden="true" />
							List your agent
						</button>
					</div>
				) : (
					<div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
						{agents.map((agent) => (
							<AgentCard key={agent.id} agent={agent} />
						))}
					</div>
				)}
			</main>

			<PublicFooter />
		</PublicPageLayout>
	);
}
